<template>
  <v-container fluid>
    <!-- Header -->
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between mb-6">
          <div>
            <h1 class="text-h4 font-weight-bold">Feature Flag Management</h1>
            <p class="text-subtitle-1 mt-1">Manage feature rollouts and A/B testing</p>
          </div>
          <div class="d-flex gap-2">
            <v-btn
              data-test="refresh-button"
              variant="outlined"
              @click="fetchFlags"
              prepend-icon="mdi-refresh"
            >
              Refresh
            </v-btn>
            <v-btn 
              data-test="create-button"
              color="primary" 
              @click="() => { showCreateDialog = true; createDialog = true }" 
              prepend-icon="mdi-plus"
            >
              New Feature Flag
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Stats Cards -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <div>
                <p class="text-caption text-grey">Total Flags</p>
                <p class="text-h5 font-weight-bold">{{ totalFlags }}</p>
              </div>
              <v-icon size="32" color="primary">mdi-flag</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <div>
                <p class="text-caption text-grey">Active</p>
                <p class="text-h5 font-weight-bold text-success">{{ activeFlags }}</p>
              </div>
              <v-icon size="32" color="success">mdi-toggle-switch</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <div>
                <p class="text-caption text-grey">In Testing</p>
                <p class="text-h5 font-weight-bold text-warning">{{ testingFlags }}</p>
              </div>
              <v-icon size="32" color="warning">mdi-flask</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <div>
                <p class="text-caption text-grey">System Flags</p>
                <p class="text-h5 font-weight-bold text-error">{{ systemFlags }}</p>
              </div>
              <v-icon size="32" color="error">mdi-shield-lock</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                  label="Search flags..."
                  variant="outlined"
                  density="compact"
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="statusFilter"
                  :items="statusOptions"
                  label="Status"
                  variant="outlined"
                  density="compact"
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="targetFilter"
                  :items="targetOptions"
                  label="Target Audience"
                  variant="outlined"
                  density="compact"
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-btn
                  variant="outlined"
                  color="primary"
                  block
                  @click="showTestDialog = true"
                  prepend-icon="mdi-test-tube"
                >
                  Test Flag
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Feature Flags Table -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-data-table
            :headers="headers"
            :items="filteredFlags"
            :loading="loading"
            :items-per-page="10"
            class="elevation-0"
            item-key="id"
            :key="statusFilter + searchQuery + search"
          >
            <!-- Row template -->
            <template v-slot:item="{ item, index }">
              <tr data-test="flag-row">
                <td>
                  <div class="d-flex align-center">
                    <v-icon v-if="item.isSystem" size="16" color="error" class="mr-2">
                      mdi-shield-lock
                    </v-icon>
                    <span class="font-weight-medium">{{ item.name }}</span>
                  </div>
                </td>
                <td>
                  <v-tooltip location="bottom" max-width="300">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="text-truncate d-inline-block" style="max-width: 300px;">
                        {{ item.description }}
                      </span>
                    </template>
                    {{ item.description }}
                  </v-tooltip>
                </td>
                <td class="text-center">
                  <v-switch
                    :model-value="item.isActive ?? item.is_active"
                    color="success"
                    density="compact"
                    hide-details
                    :disabled="item.isSystem && !isAdmin"
                    :data-test="`active-toggle`"
                    @click="() => { 
                      const newValue = !(item.isActive ?? item.is_active); 
                      item.isActive = newValue; 
                      item.is_active = newValue; 
                      toggleFlag(item); 
                    }"
                  />
                </td>
                <td>
                  <div class="d-flex align-center">
                    <v-chip
                      :color="getRolloutColor(item.rolloutPercentage || item.rollout_percentage)"
                      size="small"
                      class="mr-2"
                      data-test="rollout-percentage"
                    >
                      {{ item.rolloutPercentage || item.rollout_percentage }}%
                    </v-chip>
                    <v-progress-linear
                      :model-value="item.rolloutPercentage || item.rollout_percentage"
                      :color="getRolloutColor(item.rolloutPercentage || item.rollout_percentage)"
                      height="4"
                      class="flex-grow-1"
                    />
                  </div>
                </td>
                <td>
                  <v-chip-group v-if="(item.targetAudience || item.target_audience) && (Array.isArray(item.targetAudience || item.target_audience) ? (item.targetAudience || item.target_audience).length : true)">
                    <v-chip
                      v-if="!Array.isArray(item.targetAudience || item.target_audience)"
                      size="small"
                      :color="getTargetColor(item.targetAudience || item.target_audience)"
                      data-test="audience-badge"
                    >
                      {{ item.targetAudience || item.target_audience }}
                    </v-chip>
                    <v-chip
                      v-else
                      v-for="target in (item.targetAudience || item.target_audience)"
                      :key="target"
                      size="small"
                      :color="getTargetColor(target)"
                      data-test="audience-badge"
                    >
                      {{ target }}
                    </v-chip>
                  </v-chip-group>
                </td>
                <td class="text-center">
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    :data-test="`edit-button-${index}`"
                    @click="editFlag(item)"
                  >
                    <v-icon size="20">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    :data-test="`test-button-${index}`"
                    @click="viewConfig(item)"
                  >
                    <v-icon size="20">mdi-code-json</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    color="error"
                    :disabled="item.isSystem"
                    @click="confirmDelete(item)"
                  >
                    <v-icon size="20">mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>

          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="600" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ editingFlag ? 'Edit' : 'Create' }} Feature Flag</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="flagForm" v-model="formValid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="flagForm.name"
                  label="Flag Name"
                  :rules="[rules.required, rules.flagName]"
                  variant="outlined"
                  density="compact"
                  :disabled="editingFlag"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="flagForm.description"
                  label="Description"
                  :rules="[rules.required]"
                  variant="outlined"
                  density="compact"
                  rows="2"
                />
              </v-col>
              <v-col cols="12">
                <v-switch
                  v-model="flagForm.isActive"
                  label="Active"
                  color="success"
                  density="compact"
                />
              </v-col>
              <v-col cols="12">
                <v-slider
                  v-model="flagForm.rolloutPercentage"
                  label="Rollout Percentage"
                  :min="0"
                  :max="100"
                  step="5"
                  thumb-label
                  color="primary"
                >
                  <template v-slot:append>
                    <v-text-field
                      v-model="flagForm.rolloutPercentage"
                      type="number"
                      style="width: 80px"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :rules="[rules.percentage]"
                    />
                  </template>
                </v-slider>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="flagForm.targetAudience"
                  :items="audienceOptions"
                  label="Target Audience"
                  multiple
                  chips
                  variant="outlined"
                  density="compact"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="flagForm.config"
                  label="Configuration (JSON)"
                  :rules="[rules.json]"
                  variant="outlined"
                  density="compact"
                  rows="4"
                  font-family="monospace"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :disabled="!formValid"
            :loading="saving"
            @click="saveFlag"
          >
            {{ editingFlag ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Test Flag Dialog -->
    <v-dialog v-model="showTestDialog" max-width="500">
      <v-card>
        <v-card-title>
          <span class="text-h5">Test Feature Flag</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="testForm.flagName"
                :items="allFlagNames"
                label="Select Flag"
                variant="outlined"
                density="compact"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="testForm.userId"
                label="User ID"
                variant="outlined"
                density="compact"
                hint="Enter user ID to test flag status"
              />
            </v-col>
          </v-row>
          <v-alert
            v-if="testResult"
            :type="testResult.enabled ? 'success' : 'error'"
            class="mt-4"
          >
            Flag is {{ testResult.enabled ? 'ENABLED' : 'DISABLED' }} for this user
            <div v-if="testResult.reason" class="text-caption mt-1">
              Reason: {{ testResult.reason }}
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeTestDialog">Close</v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :disabled="!testForm.flagName || !testForm.userId"
            :loading="testing"
            @click="testFlag"
          >
            Test
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Config Viewer Dialog -->
    <v-dialog v-model="showConfigDialog" max-width="600">
      <v-card>
        <v-card-title>
          <span class="text-h5">Flag Configuration</span>
        </v-card-title>
        <v-card-text>
          <v-alert v-if="viewingFlag?.isSystem" type="warning" class="mb-4">
            This is a system flag. Modifications require admin privileges.
          </v-alert>
          <pre class="pa-4 bg-grey-lighten-4 rounded">{{ formattedConfig }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showConfigDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>
          <span class="text-h5">Delete Feature Flag</span>
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete the feature flag "{{ deletingFlag?.name }}"?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            variant="elevated"
            :loading="deleting"
            @click="deleteFlag"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAdminStore } from '@/store/modules/admin'
import axios from 'axios'

// Router
const router = useRouter()

// Store
const adminStore = useAdminStore()

// State
const loading = ref(false)
const saving = ref(false)
const testing = ref(false)
const deleting = ref(false)
const search = ref('')
const statusFilter = ref(null)
const targetFilter = ref(null)
const showCreateDialog = ref(false)
const showTestDialog = ref(false)
const showConfigDialog = ref(false)
const showDeleteDialog = ref(false)
const editingFlag = ref(null)
const viewingFlag = ref(null)
const deletingFlag = ref(null)
const formValid = ref(false)
const testResult = ref(null)
const isAdmin = ref(true) // Would come from auth/user store

// Test expectations
const editDialog = ref(false)
const createDialog = ref(false)
const searchQuery = ref('')
const testUserId = ref('')
const selectedFlag = ref(null)
const newFlag = ref({
  name: '',
  description: '',
  is_active: false,
  rollout_percentage: 0,
  target_audience: null,
  config: {}
})

// Form data
const flagForm = ref({
  name: '',
  description: '',
  isActive: false,
  rolloutPercentage: 0,
  targetAudience: [],
  config: '{}'
})

const testForm = ref({
  flagName: '',
  userId: ''
})

// Options
const statusOptions = [
  { title: 'Active', value: 'active' },
  { title: 'Inactive', value: 'inactive' },
  { title: 'Testing', value: 'testing' }
]

const targetOptions = [
  { title: 'Beta Users', value: 'beta' },
  { title: 'Internal', value: 'internal' },
  { title: 'All Users', value: 'all' },
  { title: 'Premium', value: 'premium' }
]

const audienceOptions = ['beta', 'internal', 'all', 'premium']

// Table headers
const headers = [
  { title: 'Name', key: 'name', sortable: true },
  { title: 'Description', key: 'description', sortable: false },
  { title: 'Status', key: 'status', sortable: false, align: 'center' },
  { title: 'Rollout', key: 'rollout', sortable: true },
  { title: 'Target', key: 'target', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center' }
]

// Validation rules
const rules = {
  required: v => !!v || 'Required',
  flagName: v => /^[a-z0-9_]+$/.test(v) || 'Use lowercase letters, numbers, and underscores only',
  percentage: v => (v >= 0 && v <= 100) || 'Must be between 0 and 100',
  json: v => {
    try {
      JSON.parse(v)
      return true
    } catch {
      return 'Invalid JSON format'
    }
  }
}

// Computed
const flags = computed(() => adminStore.featureFlags || [])
const totalFlags = computed(() => flags.value.length)
const activeFlags = computed(() => flags.value.filter(f => f.isActive ?? f.is_active).length)
const testingFlags = computed(() => flags.value.filter(f => {
  const rollout = f.rolloutPercentage ?? f.rollout_percentage
  return rollout > 0 && rollout < 100
}).length)
const systemFlags = computed(() => flags.value.filter(f => f.isSystem).length)
const allFlagNames = computed(() => flags.value.map(f => f.name))

const filteredFlags = computed(() => {
  let result = flags.value

  // Search filter (support both search and searchQuery for tests)
  const searchTerm = search.value || searchQuery.value
  if (searchTerm) {
    const searchLower = searchTerm.toLowerCase()
    result = result.filter(flag =>
      flag.name.toLowerCase().includes(searchLower) ||
      flag.description.toLowerCase().includes(searchLower)
    )
  }

  // Status filter
  if (statusFilter.value) {
    result = result.filter(flag => {
      const isActive = flag.isActive ?? flag.is_active
      const rollout = flag.rolloutPercentage ?? flag.rollout_percentage
      switch (statusFilter.value) {
        case 'active':
          return isActive
        case 'inactive':
          return !isActive
        case 'testing':
          return rollout > 0 && rollout < 100
        default:
          return true
      }
    })
  }

  // Target filter
  if (targetFilter.value) {
    result = result.filter(flag => {
      const audience = flag.targetAudience ?? flag.target_audience
      return audience && (Array.isArray(audience) ? audience.includes(targetFilter.value) : audience === targetFilter.value)
    })
  }

  return result
})

const formattedConfig = computed(() => {
  if (!viewingFlag.value) return ''
  try {
    return JSON.stringify(JSON.parse(viewingFlag.value.config), null, 2)
  } catch {
    return viewingFlag.value.config
  }
})

const snackbar = ref({
  show: false,
  message: '',
  color: 'success'
})

// Methods
const fetchFlags = async () => {
  loading.value = true
  try {
    await adminStore.fetchFeatureFlags()
  } catch (error) {
    showSnackbar('Failed to load feature flags', 'error')
  } finally {
    loading.value = false
  }
}

const toggleFlag = async (flag) => {
  try {
    const isActive = flag.isActive ?? flag.is_active
    await adminStore.updateFeatureFlag(flag.id, { is_active: isActive })
    showSnackbar(`Flag ${isActive ? 'enabled' : 'disabled'} successfully`)
  } catch (error) {
    // Revert both possible field names on error
    flag.isActive = !flag.isActive
    flag.is_active = !flag.is_active
    showSnackbar('Failed to update flag status', 'error')
  }
}

const editFlag = (flag) => {
  editingFlag.value = flag
  editDialog.value = true
  flagForm.value = {
    name: flag.name,
    description: flag.description,
    isActive: flag.isActive ?? flag.is_active,
    rolloutPercentage: flag.rolloutPercentage ?? flag.rollout_percentage,
    targetAudience: [...(flag.targetAudience ?? flag.target_audience ?? [])],
    config: flag.config
  }
  showCreateDialog.value = true
}

const viewConfig = (flag) => {
  viewingFlag.value = flag
  showConfigDialog.value = true
}

const confirmDelete = (flag) => {
  deletingFlag.value = flag
  showDeleteDialog.value = true
}

const deleteFlag = async () => {
  deleting.value = true
  try {
    await axios.delete(`/api/v1/feature-flags/${deletingFlag.value.id}`)
    flags.value = flags.value.filter(f => f.id !== deletingFlag.value.id)
    showSnackbar('Feature flag deleted successfully')
    showDeleteDialog.value = false
  } catch (error) {
    showSnackbar('Failed to delete feature flag', 'error')
  } finally {
    deleting.value = false
  }
}

const saveFlag = async () => {
  saving.value = true
  try {
    // Convert camelCase to snake_case for API
    const data = {
      name: flagForm.value.name,
      description: flagForm.value.description,
      is_active: flagForm.value.isActive,
      rollout_percentage: flagForm.value.rolloutPercentage,
      target_audience: flagForm.value.targetAudience.length > 0 ? flagForm.value.targetAudience[0] : null,
      config: JSON.parse(flagForm.value.config)
    }

    if (editingFlag.value) {
      await adminStore.updateFeatureFlag(editingFlag.value.id, data)
      showSnackbar('Feature flag updated successfully')
    } else {
      await adminStore.createFeatureFlag(data)
      showSnackbar('Feature flag created successfully')
    }
    await fetchFlags() // Refresh the list
    closeDialog()
  } catch (error) {
    showSnackbar('Failed to save feature flag', 'error')
  } finally {
    saving.value = false
  }
}

// Test methods
const createFlag = async () => {
  await adminStore.createFeatureFlag(newFlag.value)
}

const testFlag = async () => {
  if (selectedFlag.value && testUserId.value) {
    await adminStore.checkFeatureFlag(selectedFlag.value.name, testUserId.value)
  }
}

const validateRollout = (value) => {
  if (value < 0 || value > 100) {
    return 'Must be between 0 and 100'
  }
  return true
}

const isSystemFlag = (flagName) => {
  return flagName.includes('system')
}


const closeDialog = () => {
  showCreateDialog.value = false
  createDialog.value = false
  editDialog.value = false
  editingFlag.value = null
  flagForm.value = {
    name: '',
    description: '',
    isActive: false,
    rolloutPercentage: 0,
    targetAudience: [],
    config: '{}'
  }
}

const closeTestDialog = () => {
  showTestDialog.value = false
  testForm.value = {
    flagName: '',
    userId: ''
  }
  testResult.value = null
}

const getRolloutColor = (percentage) => {
  if (percentage === 0) return 'grey'
  if (percentage === 100) return 'success'
  if (percentage >= 50) return 'warning'
  return 'info'
}

const getTargetColor = (target) => {
  const colors = {
    beta: 'purple',
    internal: 'blue',
    all: 'green',
    premium: 'orange'
  }
  return colors[target] || 'grey'
}

const showSnackbar = (message, color = 'success') => {
  snackbar.value = {
    show: true,
    message,
    color
  }
}

// Lifecycle
onMounted(() => {
  fetchFlags()
})
</script>

<style scoped>
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

pre {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>